import React, {useState} from 'react';

import cs from 'classnames';

import {ActionsButton} from 'app/general/widgets/ActionsButton';
import {CreateItemForm} from 'app/kbs/components/KBEditPage/KBTree/KBTreeArticleItem';
import {useAddQuizLink, useRemoveQuizLink} from 'app/quizzes/api';
import {POLICIES_API, PROCEDURES_API_URL, PROCESS_API_URL} from 'app/urls';
import {getContentType} from 'app/utils/content-type';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';

import {
  BaseDocumentContentType,
  QuizSerializedType,
  SearchSerializedType,
} from 'sweet-types';

interface RecommendedDocumentsProps {
  quiz: QuizSerializedType;
  ListItem: React.ElementType;
  children?: React.ReactNode;
}

const BaseRecommendedDocuments = ({
  quiz,
  children,
  ListItem,
}: RecommendedDocumentsProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const toggleDocumentsHidden = (
    event: React.SyntheticEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    setIsVisible((prev) => !prev);
  };

  return (
    <>
      <div className="mt-4 card">
        <div className="d-flex align-items-start justify-content-between card-header">
          <div>
            <h4 className={cs('text-truncate', {'text-muted': !isVisible})}>
              {gt('Recommended Documents')}
            </h4>
            {isVisible ? (
              <p className="mb-0">
                {gt(
                  'Read these recommended documents before starting the quiz.'
                )}
              </p>
            ) : null}
          </div>
          <button
            className="btn btn-link ml-2 text-secondary p-0"
            type="button"
            onClick={toggleDocumentsHidden}
          >
            {isVisible ? (
              <>
                <span className="d-none d-sm-inline mr-1">{gt('Hide')}</span>
                <i className="fa fa-eye-slash" />
              </>
            ) : (
              <>
                <span className="d-none d-sm-inline mr-1">{gt('Show')}</span>
                <i className="fa fa-eye" />
              </>
            )}
          </button>
        </div>
        {isVisible ? (
          <ol className="list-group list-group-flush">
            <>
              {quiz.links.length === 0 && (
                <li className="list-group-item">
                  {gt(
                    'There are no recommended documents attached to this quiz.'
                  )}
                </li>
              )}
              {quiz.links.length > 0 &&
                quiz.links.map((document) => (
                  <ListItem key={`${document.url}`} document={document} />
                ))}
              {children}
            </>
          </ol>
        ) : null}
      </div>
    </>
  );
};

const AddDocument = ({quiz}: {quiz: QuizSerializedType}) => {
  const [addDoc, setAddDoc] = useState(false);
  const [addQuizLink] = useAddQuizLink();

  const documentURLs = {
    policy: POLICIES_API,
    procedure: PROCEDURES_API_URL,
    proceduregroup: PROCESS_API_URL,
  };

  const onCreate = (value: SearchSerializedType) => {
    return addQuizLink({
      quiz: quiz.url,
      document: `${documentURLs[value.content_type]}${value.object_id}/`,
    }).unwrap();
  };
  return addDoc ? (
    <div className="px-0 flex-grow-1 d-flex align-items-center">
      <i className="fas fa-plus-circle mr-2" />
      <CreateItemForm
        onSubmit={onCreate}
        onCancel={() => setAddDoc(false)}
        excludeParameter={{
          exclude_quiz_id: quiz.hashid,
        }}
        contentTypes={['procedure', 'policy', 'proceduregroup']}
        placeholderText={gt('Select a procedure, process or policy to add...')}
      />
    </div>
  ) : (
    <button
      className="btn btn-link p-0"
      type="button"
      onClick={() => setAddDoc(true)}
    >
      {gt('Add document')}
    </button>
  );
};

type ReadingProps = {
  document: {
    name: string;
    url: string;
    html_url: string;
    link_url: string;
    content_type: BaseDocumentContentType;
  };
  children?: React.ReactNode;
};

const ReadingContent = ({document, children}: ReadingProps) => {
  const ct = getContentType(document.content_type);

  return (
    <li className="list-group-item d-flex justify-content-between">
      <a href={document.html_url} target="_blank" rel="noreferrer">
        <i className={cs('far mr-1', ct.icon)} />
        {getObjectName(document, document.content_type, true)}
      </a>
      {children}
    </li>
  );
};

const EditReadingContent = ({document}: Pick<ReadingProps, 'document'>) => {
  const [removeQuizLink] = useRemoveQuizLink();

  const availableActions = [
    {
      key: 'far fa-trash',
      icon: 'far fa-trash',
      text: gt('Remove Document'),
      callback: () => removeQuizLink({url: document.link_url}).unwrap(),
    },
  ];

  return (
    <ReadingContent document={document}>
      <ActionsButton actions={availableActions} />
    </ReadingContent>
  );
};

export const RecommendedDocuments = ({
  quiz,
}: Pick<RecommendedDocumentsProps, 'quiz'>) => (
  <BaseRecommendedDocuments quiz={quiz} ListItem={ReadingContent} />
);

export const EditRecommendedDocuments = ({
  quiz,
}: Pick<RecommendedDocumentsProps, 'quiz'>) => (
  <BaseRecommendedDocuments quiz={quiz} ListItem={EditReadingContent}>
    <li className="list-group-item">
      <AddDocument quiz={quiz} />
    </li>
  </BaseRecommendedDocuments>
);
